/**
 * @file PopulationGroupList.js
 * @description Displays a list of custom population groups with functionality to manage them.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import style from './PopulationGroup.module.scss';
import Spinner from '../ui/spinners/Spinner';
import PaginationComponent from '../helpers/PaginationComponent';
import Toast from '../ui/toasts/Toast';
import { fetchPopulationGroupIndividuals } from '../../services/populationGroupService';
import styles from './PopulationGroup.module.scss';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import SearchComponent from '../helpers/search/SearchComponent';
import PopulationGroupDownloadMenu from './PopulationGroupDownloadMenu';
import { getText } from '../../i18n';

const PopulationGroupIndividualList = ({ populationGroup, onReturnToList }) => {
    const SortDirections = {
        ASCENDING: 'asc',
        DESCENDING: 'desc',
    };

    const [individuals, setIndividuals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const groupId = populationGroup.id;
    const pageSize = 20;
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    useEffect(() => {
        loadIndividuals();
    }, [currentPage]);

    useEffect(() => {
        if (!initialLoadComplete) return;

        const timeoutId = setTimeout(() => {
            loadIndividuals();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, sortConfig]);

    const loadIndividuals = async () => {
        setIsLoading(true);
        try {
            const results = await fetchPopulationGroupIndividuals(
                groupId,
                currentPage,
                pageSize,
                searchTerm,
                sortConfig.key + sortConfig.direction,
            );
            setIndividuals(results.data);
            setTotalPages(Math.ceil(results.recordCount / pageSize));
            setInitialLoadComplete(true);
        } catch (e) {
            Toast.error(getText('msg_a296e8936da6'));
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = key => {
        setSortConfig({
            key,
            direction:
                sortConfig.key === key &&
                sortConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        });
    };

    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className={`px-2 ${style.sortArrowIcon}`}>
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const handleSearch = term => {
        setSearchTerm(term);
    };

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex justify-content-between align-items-center pb-3">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {populationGroup.name}
                        </p>

                        <div className="float-end d-flex align-items-center">
                            <PopulationGroupDownloadMenu
                                populationGroup={populationGroup}
                                variant="text"
                                sort={sortConfig.key + sortConfig.direction}
                            />
                            <span
                                type="button"
                                onClick={onReturnToList}
                                className="ing-text-buttons mx-4"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <p className="float-start ms-4 ing-text-heading4">
                            {getText('msg_af4eda37487f')}
                        </p>
                    </div>
                    <div className="ms-2 mb-4">
                        <SearchComponent
                            onSearch={event => handleSearch(event)}
                            placeholder={getText('msg_bdb6594194a2')}
                            initialValue={searchTerm}
                        />
                    </div>
                    <>
                        {individuals.length === 0 ? (
                            isLoading ? (
                                <Spinner
                                    width={50}
                                    className={styles.loadingSpinner}
                                />
                            ) : (
                                <NotFoundBox
                                    backgroundColor="#f0f4ff"
                                    title={getText('msg_aca14ec90fb5')}
                                    description={getText('msg_12987c465e21')}
                                />
                            )
                        ) : null}
                        {individuals.length > 0 && (
                            <table className="table table-hover align-middle">
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th
                                            className={style.uniqueIdColumn}
                                            onClick={() =>
                                                handleSort('uniqueId')
                                            }
                                        >
                                            {getText('msg_175b3d474db1')}{' '}
                                            {renderSortArrow('uniqueId')}
                                        </th>
                                        <th
                                            onClick={() =>
                                                handleSort('lastName')
                                            }
                                        >
                                            {getText('msg_ac0e638191f6')}{' '}
                                            {renderSortArrow('lastName')}
                                        </th>
                                        <th
                                            onClick={() =>
                                                handleSort('firstName')
                                            }
                                        >
                                            {getText('msg_120ce31ac668')}{' '}
                                            {renderSortArrow('firstName')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {individuals.map(result => (
                                        <tr key={result.id}>
                                            <td
                                                className={style.uniqueIdColumn}
                                            >
                                                {result.uniqueId}
                                            </td>
                                            <td>{result.lastName}</td>
                                            <td>{result.firstName}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {totalPages > 0 && (
                            <div className="mt-auto">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        )}
                    </>
                </div>
            </div>
        </div>
    );
};

PopulationGroupIndividualList.propTypes = {
    onReturnToList: PropTypes.func.isRequired,
    populationGroup: PropTypes.object.isRequired,
};

export default PopulationGroupIndividualList;
